import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import useRevalidateOnFocus from '@/hooks/useRevalidateOnFocus';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';
import Footer from '@/components/Footer';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import PaymentTrust from '@/components/PaymentTrust';
import EditorialHero from '@/components/EditorialHero';
import LatestContentSection from '@/components/LatestContentSection';
import {
  Play, Clock, ChevronRight,
  BookOpen, MessageCircle, Heart, Newspaper, Radio,
  Eye, Crown, Check, Sparkles,
  Compass, Music, Quote, ShieldCheck, ListChecks, ArrowRight,
} from 'lucide-react';

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [recentStories, setRecentStories] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [guides, setGuides] = useState([]);
  const [confessions, setConfessions] = useState([]);
  const [articles, setArticles] = useState([]);
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState(null);
  const [editorialSeriesId, setEditorialSeriesId] = useState('');

  const fetchHomeData = useCallback(async () => {
    try {
      const [storiesRes, categoriesRes, seriesRes, guidesRes, confessionsRes, articlesRes, channelsRes, plansRes, heroRes] = await Promise.all([
        api.get('/stories?limit=15').catch(() => ({ data: [] })),
        api.get('/categories').catch(() => ({ data: [] })),
        api.get('/series').catch(() => ({ data: [] })),
        api.get('/series?series_type=guide').catch(() => ({ data: [] })),
        api.get('/confessions?limit=6').catch(() => ({ data: [] })),
        api.get('/articles?limit=6').catch(() => ({ data: [] })),
        api.get('/channels').catch(() => ({ data: [] })),
        api.get('/plans').catch(() => ({ data: null })),
        api.get('/homepage/hero').catch(() => ({ data: null })),
      ]);

      setRecentStories(storiesRes.data);
      setCategories(categoriesRes.data);
      setSeriesList(seriesRes.data);
      setGuides(guidesRes.data);
      setConfessions(confessionsRes.data);
      setArticles(articlesRes.data);
      setChannels(channelsRes.data);
      setPlans(plansRes.data);
      setEditorialSeriesId(heroRes.data?.enabled ? heroRes.data?.series?.id || '' : '');
    } catch (error) {
      console.error('Error fetching home data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHomeData(); }, [fetchHomeData]);
  useRevalidateOnFocus(fetchHomeData);

  // Price hook from /api/plans — use cheapest total plan and match the per-month equivalent.
  const priceHook = useMemo(() => {
    if (!plans) return null;
    const entries = Object.values(plans);
    if (entries.length === 0) return null;
    const cheapestTotal = entries.reduce((a, b) => (a.amount <= b.amount ? a : b));
    const totalMonths = Math.max(1, Math.round((cheapestTotal.duration_days || 30) / 30));
    const perMonth = cheapestTotal.amount / totalMonths;
    const totalLabel = `${cheapestTotal.amount.toFixed(2).replace('.', ',')} €`;
    const periodLabel = totalMonths === 1 ? '1 mês' : (totalMonths >= 12 ? '1 ano' : `${totalMonths} meses`);
    const perMonthLabel = `${perMonth.toFixed(2).replace('.', ',')} €/mês`;
    return { total: totalLabel, period: periodLabel, perMonth: perMonthLabel };
  }, [plans]);

  // Total story count derived from categories (each carries story_count)
  const totalStories = useMemo(() => {
    if (categories.length === 0) return 0;
    return categories.reduce((sum, c) => sum + (Number(c.story_count) || 0), 0);
  }, [categories]);

  // Featured series — always prefer the admin-configured editorial series
  // (matches the top hero). Falls back to the "Casa da Barragem" heuristic
  // and then to the first series in the list.
  const featuredSeries = useMemo(() => {
    if (seriesList.length === 0) return null;
    if (editorialSeriesId) {
      const editorial = seriesList.find((s) => s.id === editorialSeriesId);
      if (editorial) return editorial;
    }
    const casa = seriesList.find((s) => /casa\s+da\s+barragem/i.test(s.title || ''));
    return casa || seriesList[0];
  }, [seriesList, editorialSeriesId]);

  const otherSeries = useMemo(
    () => seriesList.filter((s) => s.id !== featuredSeries?.id).slice(0, 3),
    [seriesList, featuredSeries]
  );

  const premiumBenefits = [
    { icon: BookOpen, label: 'Acesso ilimitado a todos os contos premium' },
    { icon: Crown, label: 'Séries exclusivas' },
    { icon: Compass, label: 'Área Conhece-te completa' },
    { icon: Newspaper, label: 'Conteúdos + Prazer' },
    { icon: Quote, label: 'Frases eróticas diárias' },
    { icon: Music, label: 'Música ambiente durante a leitura' },
    { icon: ShieldCheck, label: 'Menos publicidade' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ruby" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead type="website" />
      <Navbar />

      {/* ─────────────────────────  0. EDITORIAL HERO (admin-controlled)  ───────────────────────── */}
      <EditorialHero />

      {/* ─────────────────────────  0b. LATEST CONTENT  ───────────────────────── */}
      <LatestContentSection />

      {/* ─────────────────────────  1. HERO  ───────────────────────── */}
      <section
        className="relative pt-12 md:pt-16 px-4 md:px-12 overflow-hidden"
        data-testid="home-hero"
      >
        <div className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden border border-ruby/30 bg-gradient-to-br from-black via-[#1a0508] to-ruby/80">
          {/* Decorative glow + grain */}
          <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-ruby/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-gold/15 blur-3xl pointer-events-none" />

          <div className="relative z-10 px-6 md:px-12 py-12 md:py-20 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-white/90 text-xs font-medium mb-5">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              +18 · Conteúdo Adulto Exclusivo
            </span>
            <h1
              className="font-brand text-3xl sm:text-4xl md:text-6xl font-bold leading-tight text-white max-w-4xl mx-auto mb-5"
              data-testid="home-hero-title"
            >
              Contos, séries e fantasias para os <span className="text-ruby-light">teus momentos mais íntimos</span>.
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 font-reading">
              Explora contos eróticos, séries exclusivas, confissões reais e conteúdos educativos criados para adultos.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/browse')}
                data-testid="home-hero-start-cta"
                className="bg-ruby hover:bg-ruby-light text-white rounded-full px-8 py-6 font-semibold shadow-lg active:scale-95"
              >
                <Play className="w-4 h-4 mr-2 fill-white" />
                Começar a Ler
              </Button>
              <Button
                onClick={() => navigate('/premium')}
                variant="outline"
                data-testid="home-hero-premium-cta"
                className="border-gold/50 text-gold hover:bg-gold/10 rounded-full px-8 py-6 font-semibold"
              >
                <Crown className="w-4 h-4 mr-2" />
                Explorar Premium
              </Button>
            </div>
          </div>
        </div>
      </section>

      {user && (
        <div className="px-4 md:px-12 mt-6">
          <EmailVerificationBanner />
        </div>
      )}

      {/* ─────────────────────────  2. PREMIUM BENEFITS  ───────────────────────── */}
      {!user?.is_premium && (
        <section
          className="px-4 md:px-12 mt-12 md:mt-16"
          data-testid="home-premium-benefits"
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/20 border border-gold/30 text-gold text-xs font-semibold mb-3">
                <Crown className="w-3 h-3" />
                Premium
              </span>
              <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground">
                Porque escolher o Premium?
              </h2>
              <p className="text-muted-foreground text-sm md:text-base mt-2 max-w-2xl mx-auto">
                Acesso total à plataforma, sem limites e sem distrações.
              </p>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
              {premiumBenefits.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="flex items-start gap-3 p-4 rounded-xl bg-surface border border-border hover:border-ruby/40 transition-colors"
                  data-testid={`benefit-${label.slice(0, 20)}`}
                >
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-ruby/15 border border-ruby/30 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-ruby" />
                  </span>
                  <span className="text-sm text-foreground leading-snug">{label}</span>
                </li>
              ))}
            </ul>

            <div className="text-center">
              <Button
                onClick={() => navigate('/premium')}
                data-testid="home-benefits-cta"
                className="bg-ruby hover:bg-ruby-light text-white rounded-full px-10 py-6 font-semibold shadow-lg active:scale-95"
              >
                <Crown className="w-4 h-4 mr-2" />
                Desbloquear Premium
              </Button>
              {priceHook && (
                <p className="text-xs text-muted-foreground mt-3" data-testid="home-benefits-price">
                  Desde {priceHook.total} · {priceHook.period} · equivalente a {priceHook.perMonth}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────  3. STATISTICS  ───────────────────────── */}
      <section
        className="px-4 md:px-12 mt-12 md:mt-16"
        data-testid="home-stats"
      >
        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <StatCard
            icon={BookOpen}
            value={totalStories > 0 ? `${totalStories}+` : '—'}
            label="Contos disponíveis"
            testid="stat-stories"
          />
          <StatCard
            icon={ListChecks}
            value={categories.length || '—'}
            label="Categorias"
            testid="stat-categories"
          />
          <StatCard
            icon={Crown}
            value={seriesList.length || '—'}
            label="Séries"
            testid="stat-series"
          />
          <StatCard
            icon={ShieldCheck}
            value="+18"
            label="Conteúdo exclusivo para adultos"
            testid="stat-adult"
          />
        </div>
      </section>

      {/* ─────────────────────────  4. FEATURED STORIES  ───────────────────────── */}
      {recentStories.length > 0 && (
        <section
          className="px-4 md:px-12 mt-12 md:mt-16"
          data-testid="home-stories-slider"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground">
                  Contos em destaque
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Histórias selecionadas para começares a explorar.
                </p>
              </div>
              <Link
                to="/browse"
                className="text-sm text-ruby hover:underline flex items-center gap-1 shrink-0"
              >
                Ver todos <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div
              className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {recentStories.map((story) => (
                <Link
                  key={story.id}
                  to={`/story/${story.slug || story.id}`}
                  className="snap-start shrink-0 w-[240px] md:w-[280px] group/card"
                  data-testid={`home-story-${story.id}`}
                >
                  <div className="relative h-[340px] md:h-[380px] rounded-xl overflow-hidden border border-border/50 hover:border-ruby/50 transition-all">
                    <img
                      src={story.cover_image || 'https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=600'}
                      alt={story.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                    {story.is_premium ? (
                      <Badge className="absolute top-3 right-3 bg-gold text-black text-[10px] font-bold">
                        <Crown className="w-3 h-3 mr-1" /> Premium
                      </Badge>
                    ) : (
                      <Badge className="absolute top-3 right-3 bg-emerald-500 text-white text-[10px] font-bold">
                        <Sparkles className="w-3 h-3 mr-1" /> Grátis
                      </Badge>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-brand text-lg font-semibold text-white mb-1 line-clamp-2">
                        {story.title}
                      </h3>
                      <p className="text-white/70 text-xs line-clamp-2 mb-2 font-reading">
                        {story.synopsis || story.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-white/70">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {story.reading_time} min
                        </span>
                        {story.category && (
                          <Badge variant="outline" className="border-ruby/40 text-ruby-light text-[10px] px-1.5 py-0 bg-black/30">
                            {story.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────  5. FEATURED SERIES  ───────────────────────── */}
      {featuredSeries && (
        <section
          className="px-4 md:px-12 mt-12 md:mt-16"
          data-testid="home-featured-series"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-ruby" /> Séries em destaque
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Histórias longas, contadas em capítulos — para mergulhares de uma vez.
                </p>
              </div>
              <Link
                to="/series"
                className="text-sm text-ruby hover:underline flex items-center gap-1 shrink-0"
              >
                Ver todas <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Hero card for featured series */}
            <Link
              to={`/series/${featuredSeries.slug || featuredSeries.id}`}
              className="block group"
              data-testid={`home-featured-series-${featuredSeries.id}`}
            >
              <div className="relative rounded-2xl overflow-hidden border border-border hover:border-ruby/50 transition-all min-h-[280px] md:min-h-[360px]">
                <img
                  src={featuredSeries.cover_image || 'https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=1600'}
                  alt={featuredSeries.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
                <div className="relative z-10 p-6 md:p-10 lg:p-12 flex flex-col justify-end min-h-[280px] md:min-h-[360px] max-w-3xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-ruby/90 text-white w-fit">Série em destaque</Badge>
                    {editorialSeriesId && featuredSeries.id === editorialSeriesId && (
                      <Badge className="bg-gold text-black w-fit uppercase tracking-wider text-[10px] font-bold" data-testid="featured-series-nova-badge">
                        Nova
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-brand text-2xl md:text-4xl font-bold text-white mb-3">
                    {featuredSeries.title}
                  </h3>
                  <p className="text-white/85 text-sm md:text-base mb-5 max-w-2xl line-clamp-3 font-reading">
                    {featuredSeries.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs md:text-sm text-white/70 mb-5">
                    <span>{featuredSeries.episode_count || 0} episódios</span>
                    <span className="text-gold flex items-center gap-1">
                      <Crown className="w-4 h-4" /> Incluído no Premium
                    </span>
                  </div>
                  <Button
                    className="bg-ruby hover:bg-ruby-light text-white rounded-full px-6 py-5 font-semibold w-fit active:scale-95"
                    data-testid="home-featured-series-cta"
                  >
                    <Play className="w-4 h-4 mr-2 fill-white" /> Ver a série
                  </Button>
                </div>
              </div>
            </Link>

            {/* Other series */}
            {otherSeries.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
                {otherSeries.map((s) => (
                  <Link key={s.id} to={`/series/${s.slug || s.id}`} className="group" data-testid={`home-series-${s.id}`}>
                    <Card className="bg-surface border-border hover:border-ruby/40 transition-all overflow-hidden">
                      <div className="relative h-40 bg-gradient-to-br from-ruby/20 to-surface">
                        {s.cover_image ? (
                          <img src={s.cover_image} alt={s.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-12 h-12 text-ruby/40" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-3 left-3 right-3">
                          <h3 className="font-semibold text-white text-lg">{s.title}</h3>
                          <div className="flex items-center gap-3 text-xs text-white/80 mt-1">
                            <span>{s.episode_count || 0} episódios</span>
                            <span className="text-gold font-semibold flex items-center gap-1">
                              <Crown className="w-3 h-3" /> Premium
                            </span>
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <p className="text-sm text-muted-foreground line-clamp-2">{s.description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─────────────────────────  6. CONHECE-TE  ───────────────────────── */}
      {guides.length > 0 && (
        <section
          className="px-4 md:px-12 mt-12 md:mt-16"
          data-testid="home-guides"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                  <Compass className="w-6 h-6 text-ruby" /> Conhece-te
                </h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Explora conteúdos criados para te ajudar a compreender melhor o teu corpo, desejos e intimidade.
                </p>
              </div>
              <Link
                to="/conhece-te"
                className="text-sm text-ruby hover:underline flex items-center gap-1 shrink-0"
              >
                Ver todos <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {guides.slice(0, 3).map((guide) => (
                <Link key={guide.id} to={`/conhece-te/${guide.id}`} className="group" data-testid={`home-guide-${guide.id}`}>
                  <Card className="bg-surface border-border hover:border-ruby/40 transition-all overflow-hidden">
                    <div className="relative h-40 bg-gradient-to-br from-surface-light to-surface">
                      {guide.cover_image ? (
                        <img
                          src={guide.cover_image}
                          alt={guide.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Compass className="w-12 h-12 text-ruby/40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      {guide.pdf_url && (
                        <Badge className="absolute top-3 right-3 bg-blue-500/80 text-white text-xs">PDF</Badge>
                      )}
                      <Badge className="absolute top-3 left-3 bg-ruby/90 text-white text-xs">Conhece-te</Badge>
                      <div className="absolute bottom-3 left-3 right-3">
                        <h3 className="font-semibold text-white line-clamp-1">{guide.title}</h3>
                        <span className="text-gold font-semibold text-xs flex items-center gap-1 mt-0.5">
                          <Crown className="w-3 h-3" /> Premium
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-3">
                      <p className="text-sm text-muted-foreground line-clamp-2">{guide.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────  7. + PRAZER (Articles)  ───────────────────────── */}
      {articles.length > 0 && (
        <section
          className="px-4 md:px-12 mt-12 md:mt-16"
          data-testid="home-articles"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                  <Newspaper className="w-6 h-6 text-ruby" /> + Prazer
                </h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Guias, artigos e conteúdos educativos para aprofundar o conhecimento sobre prazer e sexualidade.
                </p>
              </div>
              <Link
                to="/prazer"
                className="text-sm text-ruby hover:underline flex items-center gap-1 shrink-0"
              >
                Ver todos <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.slice(0, 3).map((a) => (
                <Link key={a.id} to={`/prazer/${a.slug || a.id}`} className="group" data-testid={`home-article-${a.id}`}>
                  <Card className="bg-surface border-border hover:border-ruby/40 transition-all overflow-hidden">
                    <div className="relative h-36">
                      {a.cover_image ? (
                        <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-ruby/10 to-surface flex items-center justify-center">
                          <Newspaper className="w-10 h-10 text-ruby/30" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3">
                        <Badge className="bg-black/60 text-white text-xs mb-1 backdrop-blur-sm">{a.category}</Badge>
                        <h3 className="font-semibold text-white line-clamp-1">{a.title}</h3>
                      </div>
                    </div>
                    <CardContent className="p-3">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {a.excerpt || a.content?.substring(0, 120)}
                      </p>
                      <span className="text-xs text-muted-foreground flex items-center gap-1 mt-2">
                        <Clock className="w-3 h-3" />
                        {a.reading_time || 5} min de leitura
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────  9. CONFISSÕES  ───────────────────────── */}
      {confessions.length > 0 && (
        <section
          className="px-4 md:px-12 mt-12 md:mt-16"
          data-testid="home-confessions"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                <MessageCircle className="w-6 h-6 text-ruby" /> Confissões
              </h2>
              <Link
                to="/confessions"
                className="text-sm text-ruby hover:underline flex items-center gap-1 shrink-0"
              >
                Ver todas <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {confessions.slice(0, 3).map((c) => (
                <Link key={c.id} to={`/confessions/${c.slug || c.id}`} className="group" data-testid={`home-confession-${c.id}`}>
                  <Card className="bg-surface border-border hover:border-ruby/40 transition-all h-full">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-ruby/30 text-ruby text-xs">{c.category}</Badge>
                        {c.is_anonymous && <Badge variant="outline" className="border-border text-xs">Anónimo</Badge>}
                      </div>
                      <h3 className="font-semibold text-foreground mb-2 line-clamp-1">{c.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {c.excerpt || c.content?.replace(/<[^>]+>/g, '').substring(0, 150)}
                      </p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{c.views || 0}</span>
                        <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{c.likes || 0}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────  10. LOJA INTIMATE  ───────────────────────── */}
      <section className="px-4 md:px-12 mt-12 md:mt-16 mb-14 md:mb-20" data-testid="home-loja-banner">
        <div className="relative max-w-6xl mx-auto overflow-hidden rounded-2xl border border-gold/30 bg-[#0d0a0b]">
          <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-ruby/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-gold/10 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-10 px-6 py-7 sm:px-10 md:px-12 md:py-9">
            <div className="max-w-2xl">
              <span className="inline-block rounded-full border border-gold/50 px-3 py-0.5 text-[10px] font-semibold tracking-[0.25em] text-gold">
                NOVO NA INTIMATE
              </span>
              <h2 className="mt-3 font-brand text-2xl sm:text-3xl md:text-4xl font-bold leading-tight text-foreground">
                O prazer tem um novo espaço.
              </h2>
              <p className="mt-2 text-sm md:text-base text-muted-foreground">
                Descobre a Loja Intimate. Uma seleção especial de lingerie, brinquedos e benefícios exclusivos para membros Premium.
              </p>
            </div>
            <Link
              to="/loja"
              data-testid="home-loja-cta"
              className="group inline-flex shrink-0 items-center justify-center gap-2 self-start md:self-center rounded-full bg-ruby px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-ruby-light"
            >
              Explorar a Loja
              <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────────────────────  11. FINAL PREMIUM CTA  ───────────────────────── */}
      {!user?.is_premium && (
        <section
          className="px-4 md:px-12 mt-14 md:mt-20 mb-12"
          data-testid="home-final-premium-cta"
        >
          <div className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden border border-gold/30">
            <div className="absolute inset-0 bg-gradient-to-br from-ruby via-ruby/90 to-black" />
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-28 -left-16 w-80 h-80 rounded-full bg-ruby-light/25 blur-3xl pointer-events-none" />

            <div className="relative z-10 p-8 md:p-12 lg:p-16 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold text-black text-[11px] font-bold uppercase tracking-wider mb-3">
                  <Crown className="w-3 h-3" /> Premium
                </span>
                <h2 className="font-brand text-3xl md:text-5xl font-bold text-white leading-tight mb-3">
                  Acede a tudo. <span className="text-gold">Sem limites.</span>
                </h2>
                <p className="text-white/85 text-base md:text-lg max-w-xl mb-6 font-reading">
                  Desbloqueia toda a plataforma e mergulha na biblioteca completa da Intimate.pt.
                </p>

                <ul className="grid sm:grid-cols-2 gap-2.5 mb-7 max-w-xl">
                  {[
                    'Acesso total a toda a plataforma',
                    'Escolhe entre 3 meses, 6 meses ou 1 ano',
                    'Novos conteúdos adicionados regularmente',
                    'Sem renovação automática',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-white/90">
                      <span className="w-5 h-5 rounded-full bg-gold/25 border border-gold/40 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-gold" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => navigate('/premium')}
                    data-testid="home-final-premium-cta-btn"
                    className="bg-white text-ruby hover:bg-white/90 rounded-full px-8 py-6 font-semibold shadow-lg active:scale-95"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Tornar-me Premium
                  </Button>
                  <Button
                    onClick={() => navigate('/premium#faq')}
                    variant="outline"
                    className="border-white/30 text-white bg-transparent hover:bg-white/10 rounded-full px-6 py-6"
                    data-testid="home-final-saber-mais"
                  >
                    Saber mais
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>

                {priceHook && (
                  <p className="text-xs text-white/70 mt-3" data-testid="home-final-premium-price">
                    Desde <strong className="text-white">{priceHook.total}</strong> · {priceHook.period} · equivalente a {priceHook.perMonth}
                  </p>
                )}

                <div className="mt-4">
                  <PaymentTrust variant="dark" />
                </div>
              </div>

              <div className="hidden lg:flex flex-col gap-3 w-64 shrink-0">
                {[
                  { icon: BookOpen, label: 'Biblioteca', value: 'Ilimitada' },
                  { icon: Crown, label: 'Séries exclusivas', value: 'Incluídas' },
                  { icon: Heart, label: 'Apoia autores', value: 'Portugueses' },
                ].map(({ icon: Icon, label, value }) => (
                  <div
                    key={label}
                    className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 p-4 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gold/20 border border-gold/30 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-white/60">{label}</p>
                      <p className="text-sm font-semibold text-white">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

function StatCard({ icon: Icon, value, label, testid }) {
  return (
    <div
      className="rounded-xl bg-surface border border-border p-4 md:p-5 text-center hover:border-ruby/40 transition-colors"
      data-testid={testid}
    >
      <div className="w-10 h-10 mx-auto rounded-lg bg-ruby/15 border border-ruby/30 flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-ruby" />
      </div>
      <p className="font-brand text-2xl md:text-3xl font-bold text-foreground">{value}</p>
      <p className="text-xs md:text-sm text-muted-foreground mt-1 leading-snug">{label}</p>
    </div>
  );
}
